import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import { collection, addDoc, getDocs, query, where, orderBy, serverTimestamp } from "firebase/firestore";
import { useSchoolId } from "../lib/auth/schoolContext";
import { Icons } from "../lib/Icons";
import { suggestTermlyTargets } from "../lib/functions";
import {
  HALF_TERMS,
  getAcademicYear,
  getCurrentHalfTerm,
  academicYearsMatch,
  getRecordHalfTerm,
} from "../lib/termlyTargets";

export default function TermlyTargets({ studentId }) {
  const { schoolId } = useSchoolId();
  const [year, setYear] = useState(getAcademicYear());
  const [halfTerm, setHalfTerm] = useState(getCurrentHalfTerm());
  const [targetBoxes, setTargetBoxes] = useState([""]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [genBusy, setGenBusy] = useState(false);
  const [genError, setGenError] = useState("");

  const load = async () => {
    try {
      setLoading(true);
      const q1 = query(
        collection(db, "termlyTargets"),
        where("studentId", "==", studentId),
        where("schoolId", "==", schoolId),
        orderBy("createdAt", "desc")
      );
      const snap = await getDocs(q1);
      const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setHistory(items);
      const current = items.find((i) => academicYearsMatch(i.academicYear, year) && getRecordHalfTerm(i) === halfTerm);
      setTargetBoxes(Array.isArray(current?.targets) && current.targets.length ? current.targets : [""]);
    } catch (e) {
      console.warn("Failed to load termly targets", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (studentId && schoolId) load();
  }, [studentId, schoolId]);

  useEffect(() => {
    const current = history.find((i) => academicYearsMatch(i.academicYear, year) && getRecordHalfTerm(i) === halfTerm);
    if (current && Array.isArray(current.targets)) setTargetBoxes(current.targets);
    else setTargetBoxes([""]);
  }, [year, halfTerm, history]);

  const save = async () => {
    const list = targetBoxes.map((s) => String(s || "").trim()).filter(Boolean);
    if (!list.length) {
      alert("Enter at least one target");
      return;
    }
    try {
      setSaving(true);
      await addDoc(collection(db, "termlyTargets"), {
        studentId,
        schoolId,
        academicYear: year,
        halfTerm,
        targets: list,
        createdAt: serverTimestamp(),
      });
      await load();
    } catch (e) {
      alert("Failed to save termly targets. See console.");
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const generate = async () => {
    setGenError("");
    try {
      setGenBusy(true);
      const result = await suggestTermlyTargets({ studentId, academicYear: year, halfTerm });
      const suggested = Array.isArray(result.data?.targets) ? result.data.targets : [];
      if (suggested.length === 0) {
        setGenError("Cannot generate: more information needed. Input EHCP, professional recommendations or progress data.");
        return;
      }
      setTargetBoxes(suggested.slice(0, 6));
    } catch (e) {
      console.error("AI generate termly targets failed", e);
      setGenError("Cannot generate: more information needed. Input EHCP, professional recommendations or progress data.");
    } finally {
      setGenBusy(false);
    }
  };

  const addBox = () => setTargetBoxes((prev) => [...prev, ""]);
  const updateBox = (idx, val) => {
    setTargetBoxes((prev) => prev.map((t, i) => (i === idx ? val : t)));
  };
  const removeBox = (idx) => {
    setTargetBoxes((prev) => prev.filter((_, i) => i !== idx));
  };

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <h3 className="text-lg font-semibold mb-2 flex items-center"><Icons.Target className="w-5 h-5 mr-2" /> Termly target generator</h3>
      <p className="text-sm text-gray-600 mb-4">
        Generated targets are based on current attainment, need and other data input such as professional reports. Targets must be varied by the SENCO.
      </p>
      <p className="text-sm text-gray-500 mb-4">
        Past terms are recorded in Individual Learning Targets above.
      </p>
      {loading ? (
        <p className="text-sm text-gray-500">Loading...</p>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Academic Year</label>
              <input className="w-full border rounded px-2 py-2 text-sm" value={year} onChange={(e) => setYear(e.target.value)} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Half Term</label>
              <select className="w-full border rounded px-2 py-2 text-sm" value={halfTerm} onChange={(e) => setHalfTerm(e.target.value)}>
                {HALF_TERMS.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-3">
            <label className="block text-sm font-medium text-gray-700 mb-2">Targets for {halfTerm} {year}</label>
            <div className="space-y-2">
              {targetBoxes.map((t, i) => (
                <div key={i} className="flex items-start gap-2">
                  <textarea
                    rows={2}
                    className="flex-1 border rounded px-2 py-2 text-sm resize-none overflow-y-auto max-h-24"
                    placeholder={`Target ${i + 1}`}
                    value={t}
                    onChange={(e) => updateBox(i, e.target.value)}
                  />
                  {targetBoxes.length > 1 && (
                    <button type="button" onClick={() => removeBox(i)} className="px-2 py-1 text-xs rounded bg-gray-200 hover:bg-gray-300">Remove</button>
                  )}
                </div>
              ))}
              <button type="button" onClick={addBox} className="px-3 py-1 bg-gray-100 hover:bg-gray-200 rounded text-sm">+ Add another target</button>
            </div>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <button onClick={save} disabled={saving} className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50">
              {saving ? "Saving..." : <><Icons.Save className="w-4 h-4 mr-1 inline-block" /> Save Targets</>}
            </button>
            <button onClick={generate} disabled={genBusy} className="px-3 py-1 bg-purple-600 text-white rounded text-sm hover:bg-purple-700 disabled:opacity-50">
              {genBusy ? "Generating..." : <><Icons.Sparkles className="w-4 h-4 mr-1 inline-block" /> Generate AI Suggestions</>}
            </button>
            {genError && <span className="text-xs text-red-600">{genError}</span>}
          </div>
        </>
      )}
    </div>
  );
}
